import { useId, type ReactNode } from 'react';
import { useBrand } from '../lib/brand';
import { draftPushes, feeCard, heroPushes, inAppRows, type NoticeSample } from './realNotifications';

export type StageTone = 'tide' | 'pool' | 'sunset' | 'green';
export type DeviceKind = 'macbook' | 'phone' | 'tablet';
export type StageTilt = 'left' | 'right';
export type StageFloat = 'note' | 'inbox' | 'result';

type Companion = {
  image: string;
  alt: string;
  device: 'phone' | 'tablet';
  avif?: string;
};

function Shot({
  src,
  alt,
  width,
  height,
  priority = false,
  srcSet,
  sizes,
  avifSrcSet,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  priority?: boolean;
  srcSet?: string;
  sizes?: string;
  avifSrcSet?: string;
}) {
  const img = (
    <img
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={alt}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
      fetchPriority={priority ? 'high' : 'auto'}
    />
  );
  if (!avifSrcSet) return img;
  return (
    <picture>
      <source type="image/avif" srcSet={avifSrcSet} sizes={sizes} />
      {img}
    </picture>
  );
}

export function MacBook({
  src,
  alt,
  priority = false,
  tilt,
  srcSet,
  sizes,
  avifSrcSet,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  tilt?: StageTilt | null;
  srcSet?: string;
  sizes?: string;
  avifSrcSet?: string;
}) {
  const tiltClass = tilt === 'left' ? 'mkt-tilt-left' : tilt === 'right' ? 'mkt-tilt-right' : '';
  return (
    <figure className={`mkt-macbook ${tiltClass}`.trim()}>
      <div className="mkt-macbook-lid">
        <span className="mkt-cam" aria-hidden />
        <div className="mkt-macbook-screen">
          <Shot src={src} alt={alt} width={1100} height={688} priority={priority} srcSet={srcSet} sizes={sizes} avifSrcSet={avifSrcSet} />
        </div>
      </div>
      <div className="mkt-macbook-chin" aria-hidden />
      <div className="mkt-macbook-deck" aria-hidden>
        <span />
      </div>
    </figure>
  );
}

export function IPhoneFrame({ children }: { children: ReactNode }) {
  return (
    <figure className="mkt-iphone">
      <div className="mkt-iphone-body">
        <span className="mkt-island" aria-hidden />
        <div className="mkt-iphone-screen">{children}</div>
      </div>
    </figure>
  );
}

function IPhone({
  src,
  alt,
  priority = false,
  avifSrcSet,
  srcSet,
  sizes,
}: {
  src: string;
  alt: string;
  priority?: boolean;
  avifSrcSet?: string;
  srcSet?: string;
  sizes?: string;
}) {
  return (
    <IPhoneFrame>
      <Shot src={src} alt={alt} width={390} height={844} priority={priority} srcSet={srcSet} sizes={sizes} avifSrcSet={avifSrcSet} />
    </IPhoneFrame>
  );
}

function IPad({ src, alt, tilt, avifSrcSet }: { src: string; alt: string; tilt?: StageTilt | null; avifSrcSet?: string }) {
  const tiltClass = tilt === 'left' ? 'mkt-tilt-left' : tilt === 'right' ? 'mkt-tilt-right' : '';
  return (
    <figure className={`mkt-ipad ${tiltClass}`.trim()}>
      <div className="mkt-ipad-body">
        <span className="mkt-cam" aria-hidden />
        <div className="mkt-ipad-screen">
          <Shot src={src} alt={alt} width={1100} height={688} avifSrcSet={avifSrcSet} />
        </div>
      </div>
    </figure>
  );
}

function Device({
  device,
  src,
  alt,
  priority,
  tilt,
  srcSet,
  sizes,
  avifSrcSet,
}: {
  device: DeviceKind;
  src: string;
  alt: string;
  priority?: boolean;
  tilt?: StageTilt | null;
  srcSet?: string;
  sizes?: string;
  avifSrcSet?: string;
}) {
  if (device === 'phone') return <IPhone src={src} alt={alt} priority={priority} avifSrcSet={avifSrcSet} srcSet={srcSet} sizes={sizes} />;
  if (device === 'tablet') return <IPad src={src} alt={alt} tilt={tilt} avifSrcSet={avifSrcSet} />;
  return <MacBook src={src} alt={alt} priority={priority} tilt={tilt} srcSet={srcSet} sizes={sizes} avifSrcSet={avifSrcSet} />;
}

function PushIcon() {
  const raw = useId().replace(/:/g, '');
  return (
    <svg className="mkt-note-icon" viewBox="0 0 192 192" aria-hidden>
      <defs>
        <linearGradient id={raw} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0d4a8a" />
          <stop offset="100%" stopColor="#0a3a70" />
        </linearGradient>
      </defs>
      <rect width="192" height="192" rx="36" fill={`url(#${raw})`} />
      <path d="M44 144V48h24l28 58 28-58h24v96h-20V80L96 132 64 80v64H44z" fill="#fff" />
      <path
        d="M24 162 Q48 150 72 162 Q96 174 120 162 Q144 150 168 162"
        stroke="#4fc3f7"
        strokeWidth="6"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IosPush({ sample, appName }: { sample: NoticeSample; appName: string }) {
  return (
    <article className="mkt-note" data-notification-source={sample.source}>
      <div className="mkt-note-app">
        <PushIcon />
        <p className="mkt-note-kicker">{appName}</p>
        <p className="mkt-note-when">now</p>
      </div>
      <p className="mkt-note-title">{sample.title}</p>
      <p className="mkt-note-body">{sample.body}</p>
    </article>
  );
}

function PushStack({ samples, appName }: { samples: readonly NoticeSample[]; appName: string }) {
  return (
    <div className="mkt-note-stack">
      {samples.map((sample) => (
        <IosPush key={sample.id} sample={sample} appName={appName} />
      ))}
    </div>
  );
}

function InAppNotices() {
  return (
    <div className="mkt-inbox">
      <article className="mkt-fee" data-notification-source={feeCard.source}>
        <p className="mkt-fee-title">
          <span aria-hidden>💳</span> {feeCard.title}
        </p>
        <p className="mkt-fee-body">{feeCard.body}</p>
        <span className="mkt-dismiss">Dismiss</span>
      </article>
      <section className="mkt-inbox-list" aria-label="Notices">
        <h3 className="mkt-inbox-kicker">Notices</h3>
        <ul>
          {inAppRows.map((row) => (
            <li key={row.id} className={row.spots ? 'mkt-inbox-spots' : undefined} data-notification-source={row.source}>
              <div>
                <p className="mkt-inbox-title">{row.title}</p>
                <p className="mkt-inbox-body">{row.body}</p>
              </div>
              <span className="mkt-dismiss">Dismiss</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function ResultCard() {
  return (
    <div className="mkt-result">
      <p className="mkt-note-kicker">Sample meet</p>
      <p className="mkt-note-title">Club time trial</p>
      <p className="mkt-result-meta">100 free · Sasha Kline</p>
      <p className="mkt-result-time">
        1:08.40 <span className="mkt-pb">PB</span>
      </p>
    </div>
  );
}

export function WaterTexture() {
  return (
    <svg className="mkt-water" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMax slice" aria-hidden>
      <g fill="none" stroke="#ffffff" strokeWidth="3" opacity="0.28">
        <path d="M-20 250 C 220 210, 420 310, 720 260 S 1180 180, 1480 250" strokeDasharray="14 16" />
        <path d="M-20 360 C 260 320, 520 430, 820 360 S 1200 280, 1480 360" strokeDasharray="10 18" />
        <path d="M-20 480 C 240 440, 560 560, 860 470 S 1220 400, 1480 490" strokeDasharray="12 16" />
        <path d="M-20 600 C 280 560, 600 690, 920 590 S 1240 520, 1480 620" strokeDasharray="8 18" />
        <path d="M-20 730 C 300 680, 640 820, 980 720 S 1260 660, 1480 750" />
      </g>
      <ellipse cx="280" cy="640" rx="220" ry="36" fill="#ffffff" opacity="0.14" />
      <ellipse cx="980" cy="520" rx="260" ry="42" fill="#7ed0e8" opacity="0.28" />
      <ellipse cx="720" cy="760" rx="340" ry="48" fill="#ffffff" opacity="0.12" />
      <ellipse cx="1180" cy="700" rx="160" ry="28" fill="#ffffff" opacity="0.16" />
    </svg>
  );
}

export function HeroDevices() {
  const brand = useBrand();
  return (
    <div className="mkt-hero-devices">
      <MacBook
        src="/marketing/sessions-1440.webp"
        srcSet="/marketing/sessions-720.webp 720w, /marketing/sessions-1440.webp 1440w"
        avifSrcSet="/marketing/sessions-720.avif 720w, /marketing/sessions-1440.avif 1440w"
        sizes="(max-width: 767px) 92vw, 920px"
        alt="Sessions list for the fictional Harbourside Swim Club"
        priority
        tilt="left"
      />
      <div className="mkt-hero-phone mkt-drift-late">
        <IPhone
          src="/marketing/pwa-480.webp"
          avifSrcSet="/marketing/pwa-480.avif"
          alt="Parent app home for a fictional Harbourside family"
        />
        <PushStack samples={heroPushes} appName={brand.name} />
      </div>
    </div>
  );
}

export function ProductVisual({
  src,
  alt,
  device,
  tone,
  tilt = null,
  companion = null,
  float = null,
  priority = false,
  avifSrc,
}: {
  src: string;
  alt: string;
  device: DeviceKind;
  tone: StageTone;
  tilt?: StageTilt | null;
  companion?: Companion | null;
  float?: StageFloat | null;
  priority?: boolean;
  avifSrc?: string;
}) {
  const brand = useBrand();
  const stageClass = [
    'mkt-stage',
    `mkt-tone-${tone}`,
    companion ? 'mkt-has-companion' : '',
    device === 'phone' ? 'mkt-stage-phone' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={stageClass}>
      <div className="mkt-layout">
        {companion ? (
          <div className="mkt-companion mkt-drift-late">
            <Device device={companion.device} src={companion.image} alt={companion.alt} avifSrcSet={companion.avif} />
          </div>
        ) : null}
        <div className="mkt-primary mkt-drift">
          <Device device={device} src={src} alt={alt} priority={priority} tilt={tilt} avifSrcSet={avifSrc} />
        </div>
        {float ? (
          <div className={float === 'inbox' ? 'mkt-float mkt-float-inbox' : 'mkt-float'}>
            {float === 'note' ? <PushStack samples={draftPushes} appName={brand.name} /> : null}
            {float === 'inbox' ? <InAppNotices /> : null}
            {float === 'result' ? <ResultCard /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
